"use client";

import { safeStringify } from "@/utils/safe-stringify";
import { useSyncExternalStore } from "react";

type ConvexDebugEntryStatus = "pending" | "ready" | "error";

type ConvexDebugEntryBase = { key: string; name: string; createdAt: number; updatedAt: number; updateCount: number; timelineSeq: number; };

type ConvexDebugQueryEntry = ConvexDebugEntryBase & { kind: "query" | "paginatedQuery"; args: unknown; result: unknown; status: ConvexDebugEntryStatus; pendingAt: number | null; durationMs: number | null; };

type ConvexDebugEventEntry = ConvexDebugEntryBase & { kind: "event"; page: string; href: string | null; ts: string; };

export type ConvexDebugEntry = ConvexDebugQueryEntry | ConvexDebugEventEntry;

type ConvexDebugMarkerSource = "manual" | "click";

export type ConvexDebugMarker = { id: number; createdAt: number; source: ConvexDebugMarkerSource; timelineSeq: number; };

type ConvexDebugSnapshot = { entries: ConvexDebugEntry[]; markers: ConvexDebugMarker[]; updatedAt: number | null; };

/** Recording on/off — persisted for the debug store. */
const CONVEX_DEBUG_RECORDING_STORAGE_KEY = "disputewell.assistant.convexDebugRecording";
const CONVEX_DEBUG_MARKER_SESSION_KEY = "disputewell.assistant.convexDebugMarkers";
const CONVEX_DEBUG_EVENTS_SESSION_KEY = "disputewell.assistant.convexDebugEvents";
const CONVEX_DEBUG_MARKER_STORAGE_KEY = "disputewell.assistant.convexDebugMarker";
const CONVEX_DEBUG_EVENTS_STORAGE_KEY = "disputewell.assistant.convexDebugEvents";
const DEBUG_STORE_GLOBAL_KEY = "__dwConvexDebugStore";

type ConvexDebugStoreGlobal = { entries: Map<string, ConvexDebugEntry>; markers: ConvexDebugMarker[]; markerId: number; eventId: number; timelineSeqCounter: number; enabled: boolean; lastUpdatedAt: number | null; currentSnapshot: ConvexDebugSnapshot; emitQueued: boolean; listeners: Set<() => void>; enabledListeners: Set<() => void>; hydrated: boolean; };

const EMPTY_DEBUG_SNAPSHOT: ConvexDebugSnapshot = { entries: [], markers: [], updatedAt: null };

function createEmptyStore(): ConvexDebugStoreGlobal {
    return { entries: new Map(), markers: [], markerId: 0, eventId: 0, timelineSeqCounter: 0, enabled: false, lastUpdatedAt: null, currentSnapshot: EMPTY_DEBUG_SNAPSHOT, emitQueued: false, listeners: new Set(), enabledListeners: new Set(), hydrated: false };
}

function getGlobalStoreRoot(): ConvexDebugStoreGlobal {
    const root = globalThis as typeof globalThis & { [DEBUG_STORE_GLOBAL_KEY]?: ConvexDebugStoreGlobal; };
    if (!root[DEBUG_STORE_GLOBAL_KEY]) {
        root[DEBUG_STORE_GLOBAL_KEY] = createEmptyStore();
    }
    return root[DEBUG_STORE_GLOBAL_KEY];
}

function nextTimelineSeq(store: ConvexDebugStoreGlobal): number {
    store.timelineSeqCounter += 1;
    return store.timelineSeqCounter;
}

function normalizeError(value: unknown): unknown {
    if (value instanceof Error) {
        return { name: value.name, message: value.message, stack: value.stack };
    }
    return value;
}

/** Convex React adds a client-only paginationOpts.id on each usePaginatedQuery subscription. */
function normalizeDebugQueryArgs(args: unknown): unknown {
    if (typeof args !== "object" || args === null || Array.isArray(args)) {
        return args;
    }
    const record = args as Record<string, unknown>;
    const paginationOpts = record.paginationOpts;
    if (typeof paginationOpts !== "object" || paginationOpts === null || Array.isArray(paginationOpts)) {
        return args;
    }
    if (!("id" in paginationOpts)) {
        return args;
    }
    const { id: _id, ...restPaginationOpts } = paginationOpts as Record<string, unknown>;
    return { ...record, paginationOpts: restPaginationOpts };
}

export function buildDebugEntryKey(kind: ConvexDebugQueryEntry["kind"], name: string, args: unknown): string {
    return `${kind}:${name}:${safeStringify(normalizeDebugQueryArgs(args), String)}`;
}

function pruneStaleDebugQueryEntries(store: ConvexDebugStoreGlobal, key: string, kind: ConvexDebugQueryEntry["kind"], name: string, args: unknown) {
    const normalizedArgs = safeStringify(normalizeDebugQueryArgs(args), String);
    for (const [existingKey, existing] of store.entries) {
        if (existingKey === key || existing.kind === "event") {
            continue;
        }
        if (existing.kind !== kind || existing.name !== name) {
            continue;
        }
        if (safeStringify(normalizeDebugQueryArgs(existing.args), String) !== normalizedArgs) {
            continue;
        }
        store.entries.delete(existingKey);
    }
}

function localIsoString(date: Date): string {
    const timezoneOffsetMinutes = -date.getTimezoneOffset();
    const offsetSign = timezoneOffsetMinutes >= 0 ? "+" : "-";
    const absoluteOffsetMinutes = Math.abs(timezoneOffsetMinutes);
    const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, "0");
    const offsetMinutes = String(absoluteOffsetMinutes % 60).padStart(2, "0");
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
}

function isMarkerSource(value: unknown): value is ConvexDebugMarkerSource {
    return value === "manual" || value === "click";
}

function parseDebugMarker(value: unknown): ConvexDebugMarker | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }
    const candidate = value as Partial<ConvexDebugMarker>;
    if (typeof candidate.id !== "number" || !Number.isFinite(candidate.id) || typeof candidate.createdAt !== "number" || !Number.isFinite(candidate.createdAt) || !isMarkerSource(candidate.source)) {
        return null;
    }
    return { id: candidate.id, createdAt: candidate.createdAt, source: candidate.source, timelineSeq: typeof candidate.timelineSeq === "number" && Number.isFinite(candidate.timelineSeq) ? candidate.timelineSeq : candidate.createdAt };
}

function parseDebugEventEntry(value: unknown): ConvexDebugEventEntry | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }
    const candidate = value as Partial<ConvexDebugEventEntry> & { location?: string; };
    if (candidate.kind !== "event" || typeof candidate.key !== "string" || typeof candidate.name !== "string" || typeof candidate.ts !== "string" || typeof candidate.createdAt !== "number" || !Number.isFinite(candidate.createdAt) || typeof candidate.updatedAt !== "number" || !Number.isFinite(candidate.updatedAt) || typeof candidate.updateCount !== "number" || !Number.isFinite(candidate.updateCount)) {
        return null;
    }

    const href = typeof candidate.href === "string" && candidate.href.length > 0 ? candidate.href : null;
    const pageCandidate = typeof candidate.page === "string" && candidate.page.length > 0 ? candidate.page : (typeof candidate.location === "string" && candidate.location.length > 0 ? candidate.location : href ?? "");

    return { key: candidate.key, kind: "event", name: candidate.name, page: pageCandidate, href, ts: candidate.ts, createdAt: candidate.createdAt, updatedAt: candidate.updatedAt, updateCount: candidate.updateCount, timelineSeq: typeof candidate.timelineSeq === "number" && Number.isFinite(candidate.timelineSeq) ? candidate.timelineSeq : candidate.createdAt };
}

function readJsonArray(storage: Storage, key: string): unknown[] {
    try {
        const raw = storage.getItem(key);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function readMarkersFromSession(): ConvexDebugMarker[] {
    if (typeof window === "undefined") {
        return [];
    }

    const fromSession = readJsonArray(window.sessionStorage, CONVEX_DEBUG_MARKER_SESSION_KEY).flatMap((item) => {
        const markerEntry = parseDebugMarker(item);
        return markerEntry ? [markerEntry] : [];
    });
    if (fromSession.length > 0) {
        return fromSession;
    }

    const fromLegacy = readJsonArray(window.localStorage, CONVEX_DEBUG_MARKER_STORAGE_KEY).flatMap((item) => {
        const markerEntry = parseDebugMarker(item);
        return markerEntry ? [markerEntry] : [];
    });
    if (fromLegacy.length > 0) {
        return fromLegacy;
    }
    const legacySingle = window.localStorage.getItem(CONVEX_DEBUG_MARKER_STORAGE_KEY);
    if (legacySingle) {
        try {
            const parsed = JSON.parse(legacySingle);
            const markerEntry = parseDebugMarker(parsed);
            return markerEntry ? [markerEntry] : [];
        } catch {
            return [];
        }
    }
    return [];
}

function readEventsFromSession(): ConvexDebugEventEntry[] {
    if (typeof window === "undefined") {
        return [];
    }

    const fromSession = readJsonArray(window.sessionStorage, CONVEX_DEBUG_EVENTS_SESSION_KEY).flatMap((item) => {
        const eventEntry = parseDebugEventEntry(item);
        return eventEntry ? [eventEntry] : [];
    });
    if (fromSession.length > 0) {
        return fromSession;
    }

    return readJsonArray(window.localStorage, CONVEX_DEBUG_EVENTS_STORAGE_KEY).flatMap((item) => {
        const eventEntry = parseDebugEventEntry(item);
        return eventEntry ? [eventEntry] : [];
    });
}

function writeMarkersToSession(store: ConvexDebugStoreGlobal) {
    if (typeof window === "undefined") {
        return;
    }

    try {
        if (store.markers.length > 0) {
            window.sessionStorage.setItem(CONVEX_DEBUG_MARKER_SESSION_KEY, JSON.stringify(store.markers));
        } else {
            window.sessionStorage.removeItem(CONVEX_DEBUG_MARKER_SESSION_KEY);
        }
    } catch {
        // Debug markers should never break the app if sessionStorage is unavailable.
    }
}

function writeEventsToSession(store: ConvexDebugStoreGlobal) {
    if (typeof window === "undefined") {
        return;
    }

    try {
        const eventEntries = Array.from(store.entries.values()).filter((entry): entry is ConvexDebugEventEntry => entry.kind === "event");
        window.sessionStorage.setItem(CONVEX_DEBUG_EVENTS_SESSION_KEY, JSON.stringify(eventEntries));
    } catch {
        // Debug events should never break the app if sessionStorage is unavailable.
    }
}

function clearPersistedDebugData() {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.sessionStorage.removeItem(CONVEX_DEBUG_MARKER_SESSION_KEY);
        window.sessionStorage.removeItem(CONVEX_DEBUG_EVENTS_SESSION_KEY);
        window.localStorage.removeItem(CONVEX_DEBUG_MARKER_STORAGE_KEY);
        window.localStorage.removeItem(CONVEX_DEBUG_EVENTS_STORAGE_KEY);
    } catch {
        // Debug persistence should never break the app.
    }
}

function readRecordingEnabledFromStorage(): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    const next = window.localStorage.getItem(CONVEX_DEBUG_RECORDING_STORAGE_KEY);
    if (next !== null) {
        return next === "true";
    }
    return window.localStorage.getItem("disputewell.assistant.convexDebugPanel") === "true";
}

function maxTimelineSeq(store: ConvexDebugStoreGlobal): number {
    let maxSeq = store.timelineSeqCounter;
    for (const marker of store.markers) {
        maxSeq = Math.max(maxSeq, marker.timelineSeq);
    }
    for (const entry of store.entries.values()) {
        maxSeq = Math.max(maxSeq, entry.timelineSeq);
    }
    return maxSeq;
}

function rebuildSnapshot(store: ConvexDebugStoreGlobal) {
    store.currentSnapshot = { entries: Array.from(store.entries.values()).sort((a, b) => b.timelineSeq - a.timelineSeq), markers: [...store.markers].sort((a, b) => b.timelineSeq - a.timelineSeq), updatedAt: store.lastUpdatedAt };
}

function hydrateStore(store: ConvexDebugStoreGlobal) {
    if (store.hydrated || typeof window === "undefined") {
        return;
    }
    store.hydrated = true;
    store.enabled = readRecordingEnabledFromStorage();
    store.markers = readMarkersFromSession();
    store.markerId = store.markers.reduce((maxId, markerEntry) => Math.max(maxId, markerEntry.id), 0);
    for (const eventEntry of readEventsFromSession()) {
        store.entries.set(eventEntry.key, eventEntry);
        store.eventId += 1;
        store.lastUpdatedAt = Math.max(store.lastUpdatedAt ?? 0, eventEntry.updatedAt);
    }
    store.timelineSeqCounter = maxTimelineSeq(store);
    rebuildSnapshot(store);
}

function storeRef(): ConvexDebugStoreGlobal {
    const store = getGlobalStoreRoot();
    hydrateStore(store);
    return store;
}

function emit(store: ConvexDebugStoreGlobal) {
    rebuildSnapshot(store);
    for (const listener of store.listeners) {
        listener();
    }
}

function emitBatched(store: ConvexDebugStoreGlobal) {
    if (store.emitQueued) {
        return;
    }
    store.emitQueued = true;
    queueMicrotask(() => {
        store.emitQueued = false;
        emit(store);
    });
}

export function recordConvexDebugEntry(input: {
    kind: ConvexDebugQueryEntry["kind"];
    name: string;
    args: unknown;
    result: unknown;
    status: ConvexDebugEntryStatus;
}) {
    const store = storeRef();
    if (!store.enabled) {
        return;
    }
    const normalizedArgs = normalizeDebugQueryArgs(input.args);
    const key = buildDebugEntryKey(input.kind, input.name, normalizedArgs);
    const previous = store.entries.get(key);
    const result = normalizeError(input.result);
    if (previous && previous.kind !== "event" && previous.status === input.status && safeStringify(previous.result, String) === safeStringify(result, String)) {
        return;
    }
    pruneStaleDebugQueryEntries(store, key, input.kind, input.name, normalizedArgs);
    const updatedAt = Date.now();
    const previousQuery = previous?.kind !== "event" ? previous : undefined;
    let pendingAt = previousQuery?.pendingAt ?? null;
    let durationMs = previousQuery?.durationMs ?? null;
    if (input.status === "pending") {
        if (!previousQuery || previousQuery.status !== "pending") {
            pendingAt = updatedAt;
            durationMs = null;
        }
    } else {
        const startedAt = pendingAt ?? previousQuery?.createdAt ?? updatedAt;
        durationMs = Math.max(0, updatedAt - startedAt);
        pendingAt = null;
    }
    store.entries.set(key, { key, kind: input.kind, name: input.name, args: normalizedArgs, result, status: input.status, pendingAt, durationMs, createdAt: previousQuery?.createdAt ?? updatedAt, updatedAt, updateCount: (previousQuery?.updateCount ?? 0) + 1, timelineSeq: previousQuery?.timelineSeq ?? nextTimelineSeq(store) });
    store.lastUpdatedAt = updatedAt;
    emitBatched(store);
}

export function recordConvexDebugEvent(input: {
    name: string;
    page: string;
    href?: string | null;
}) {
    const store = storeRef();
    if (!store.enabled) {
        return;
    }
    const createdAt = Date.now();
    const key = `event:${createdAt}:${store.eventId + 1}`;
    store.eventId += 1;
    store.entries.set(key, { key, kind: "event", name: input.name, page: input.page, href: input.href ?? null, ts: localIsoString(new Date(createdAt)), createdAt, updatedAt: createdAt, updateCount: 1, timelineSeq: nextTimelineSeq(store) });
    store.lastUpdatedAt = createdAt;
    writeEventsToSession(store);
    emitBatched(store);
}

export function clearConvexDebugEntries() {
    const store = storeRef();
    store.entries.clear();
    store.markers = [];
    store.markerId = 0;
    store.eventId = 0;
    store.timelineSeqCounter = 0;
    clearPersistedDebugData();
    store.lastUpdatedAt = Date.now();
    emit(store);
}

export function markConvexDebugBoundary(source: ConvexDebugMarkerSource = "manual") {
    const store = storeRef();
    if (source === "click" && !store.enabled) {
        return;
    }
    const nextMarker = { id: store.markerId + 1, createdAt: Date.now(), source, timelineSeq: nextTimelineSeq(store) };
    store.markerId = nextMarker.id;
    store.markers.push(nextMarker);
    writeMarkersToSession(store);
    emit(store);
}

function subscribe(listener: () => void) {
    const store = storeRef();
    store.listeners.add(listener);
    return () => {
        store.listeners.delete(listener);
    };
}

function subscribeEnabled(listener: () => void) {
    const store = storeRef();
    store.enabledListeners.add(listener);
    return () => {
        store.enabledListeners.delete(listener);
    };
}

export function subscribeConvexDebugRecordingEnabled(listener: () => void): () => void {
    return subscribeEnabled(listener);
}

export function isConvexDebugRecordingEnabled(): boolean {
    return storeRef().enabled;
}

export function setConvexDebugRecordingEnabled(nextEnabled: boolean) {
    const store = storeRef();
    if (store.enabled === nextEnabled) {
        return;
    }
    store.enabled = nextEnabled;
    if (typeof window !== "undefined") {
        window.localStorage.setItem(CONVEX_DEBUG_RECORDING_STORAGE_KEY, String(nextEnabled));
    }
    for (const listener of store.enabledListeners) {
        listener();
    }
}

export function useConvexDebugSnapshot(): ConvexDebugSnapshot {
    return useSyncExternalStore(subscribe, () => storeRef().currentSnapshot, () => EMPTY_DEBUG_SNAPSHOT);
}

export function useConvexDebugRecordingEnabled(): boolean {
    return useSyncExternalStore(subscribeEnabled, () => storeRef().enabled, () => false);
}
